import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import dayjs from 'dayjs';
import { Department } from 'src/app/models/department.model';
import { PartnerContractVariableCondition } from 'src/app/models/partner-contract-variable-condition.model';
import { ApiService } from 'src/app/services/api.service';

export interface PartnerContractVariableConditionDialogData {
  contract_id: number;
  condition?: PartnerContractVariableCondition | null;
  departments: Department[];
}

@Component({
  selector: 'app-partner-contract-variable-condition-dialog',
  templateUrl: './partner-contract-variable-condition-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
  ],
})
export class PartnerContractVariableConditionDialogComponent implements OnInit {
  private readonly dialog_ref = inject(
    MatDialogRef<PartnerContractVariableConditionDialogComponent, PartnerContractVariableCondition | null>
  );
  private readonly data = inject<PartnerContractVariableConditionDialogData>(MAT_DIALOG_DATA);
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);
  private readonly destroy_ref = inject(DestroyRef);

  readonly departments = signal(this.data.departments);
  readonly saving = signal(false);
  readonly is_edit = Boolean(this.data.condition?.id);

  readonly form = this.fb.group({
    start_date: [
      this.data.condition?.start_date ? dayjs(this.data.condition.start_date).toDate() : (null as Date | null),
      Validators.required,
    ],
    end_date: [this.data.condition?.end_date ? dayjs(this.data.condition.end_date).toDate() : (null as Date | null)],
    department_ids: [(this.data.condition?.departments?.map(d => d.id) ?? []) as number[]],
  });

  ngOnInit(): void {
    if (this.departments().length === 0) {
      this.api
        .getDepartments()
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe(response => {
          this.departments.set(response.data ?? []);
        });
    }
  }

  cancel(): void {
    this.dialog_ref.close(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const body = {
      start_date: dayjs(value.start_date).format('YYYY-MM-DD'),
      end_date: value.end_date ? dayjs(value.end_date).format('YYYY-MM-DD') : null,
      department_ids: value.department_ids ?? [],
    };

    this.saving.set(true);
    const request$ = this.is_edit
      ? this.api.updatePartnerContractVariableCondition(this.data.contract_id, this.data.condition!.id, body)
      : this.api.createPartnerContractVariableCondition(this.data.contract_id, body);

    request$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe({
      next: condition => {
        this.saving.set(false);
        this.dialog_ref.close(condition);
      },
      error: () => this.saving.set(false),
    });
  }
}
